<template>
  <div class="vtimeselector">
    <input
      type="text"
      readonly="readonly"
      autocomplete="off"
      class="vtimeselector__input form-control"
      :value="timeFormated()"
      :id="id"
      :disabled="disabled"
      :required="required"
      :name="name"
      :class="{'vtimeselector__input--is-open': !pickerState.isClosed}"
      @click="togglePicker"
    />
      <div class="vtimeselector__clear" @click="clearTime">
        <slot name="clear-ico">
        <span class="vtimeselector__clear__ico">
          <font-awesome-icon class="mr-3" icon="times" />
        </span>
        </slot>
      </div>

      <div class="vtimeselector__container shadow"
           :class="{'vtimeselector__container--is-closed': pickerState.isClosed}"
      >
        <div class="vtimeselector__box">
          <ul class="vtimeselector__box__list vtimeselector__box__list--hours">
            <li class="vtimeselector__box__head">
              <slot name="hours">HH</slot>
            </li>
            <li
              class="vtimeselector__box__item vtimeselector__box__item--hours"
              v-for="(hour, index) in timeCount(interval.h, hoursLength)" :key="index"
              :class="{
                'timeselector__box__item--is-selected': pickerState.selected.hour === hour
              }"
              @click="selectTime('hour', hour, $event)">{{hour}}</li>
          </ul>
          <ul class="vtimeselector__box__list vtimeselector__box__list--minutes">
            <li class="vtimeselector__box__head">
              <slot name="minutes">mm</slot>
            </li>
            <li
              class="vtimeselector__box__item vtimeselector__box__item--minutes"
              v-for="(minute, index) in timeCount(interval.m)" :key="index"
              :class="{
                'timeselector__box__item--is-selected': pickerState.selected.minute === minute
              }"
              @click="selectTime('minute', minute, $event)">{{minute}}</li>
          </ul>
          <ul class="vtimeselector__box__list vtimeselector__box__list--seconds">
            <li class="vtimeselector__box__head">
              <slot name="seconds">ss</slot>
            </li>
            <li
              class="vtimeselector__box__item vtimeselector__box__item--seconds"
              v-for="(second, index) in timeCount(interval.s)" :key="index"
              :class="{
                'timeselector__box__item--is-selected': pickerState.selected.second === second
              }"
              @click="selectTime('second', second, $event)">{{second}}</li>
          </ul>
        </div>
        <div>
          <b-button class="m-3 float-right" @click="cancel()">{{ $t('cancel') }}</b-button>
          <b-button class="m-3 float-right" @click="setAndClose()">{{ $t('do-set') }}</b-button>
        </div>
      </div>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside';
import { parseHMSFromSeconds, timerFormatted } from '@/utils/time';
/**
 * Timeselector component. Click bellow to see more informations about the component
 * @example ../README.md
 */
export default {
  name: 'TimerSelector',
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    /**
     * Data binding
     * @model
     */
    value: {
      type: Number,
    },
    required: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
    },
    name: {
      type: String,
    },
    /**
     * Input placeholder text
     */
    placeholder: {
      type: String,
    },
    /**
     * Disable input
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Define hours, minutes and seconds interval to the picker
     */
    interval: {
      type: Object,
      default: () => ({
        h: 1,
        m: 1,
        s: 1,
      }),
    },
  },
  data() {
    return {
      originalValue: this.value,
      pickerState: {
        selected: {
          hour: null,
          minute: null,
          second: null,
        },
        isClosed: true,
        isPristine: true,
      },
      hoursLength: 24,
      separator: ' ',
    };
  },
  computed: {
    timeInSec() {
      return this.pickerState.selected.hour * 3600
        + this.pickerState.selected.minute * 60
        + this.pickerState.selected.second;
    },
  },
  methods: {
    /**
     * Generate a time format depending on formating props
     * @return {String} - formated time
     */
    timeFormated() {
      if (this.placeholder && this.pickerState.isPristine) {
        return this.placeholder;
      }
      return timerFormatted(
        this.pickerState.selected.hour,
        this.pickerState.selected.minute,
        this.pickerState.selected.second,
        this.separator,
      );
    },

    /**
     * Create time interval in list
     * @param {Number} [interval=1] - interval asked
     * @param {Number} [timeLength=60] - numbered range to work on.
     * @return {Array} - list of times to select
     * @public
     */
    timeCount(interval = 1, timeLength = 60) {
      return Array.apply(null, { length: timeLength })
        .map(Number.call, Number)
        .filter(num => num % interval === 0);
    },

    /**
     * Update of the selected time on the input
     * @param {String} time - kind of time selected (hour, minute, second)
     * @param {Object} e - event listened
     * @emits selected(Hour|Minute|Second)
     * @emits input
     * @public
     */
    selectTime(type, el, e) {
      if (!this.disabled) {
        this.pickerState.isPristine = false;
        this.pickerState.selected[type] = Number.parseInt(e.target.textContent, 10);

        /**
         * Emit selectedHour selectedMinute and selectedSecond depending on what changed
         * @event selected(Hour|Minute|Second)
         * @type {Date}
         */
        this.$emit(`selected${type.charAt(0).toUpperCase() + type.slice(1)}`, this.pickerState.selected[type]);
      }
    },

    /**
     * Clear time and reset modalbox
     * @public
     */
    clearTime() {
      this.pickerState.isPristine = true;
      this.pickerState.selected.hour = -1;
      this.pickerState.selected.minute = -1;
      this.pickerState.selected.second = -1;
      this.setPickersValue(this.originalValue);
      this.close();
    },

    /**
     * Toggle Picker in order to open or close the select modal
     * @public
     */
    togglePicker() {
      if (!this.disabled) {
        this.pickerState.isClosed = !this.pickerState.isClosed;
      }
    },
    cancel() {
      this.clearTime();
      this.close();
    },
    setAndClose() {
      console.log('input', this.timeInSec);
      /**
       * Emit event because input has changed
       * @event input
       * @type {Date|Object}
       */
      this.$emit('input', this.timeInSec);
      this.close();
    },
    /**
     * Close timepicker when user has clicked outside of the element
     * @param {Object} [e=null] - click event listened
     * @public
     */
    close(e = null) {
      if (e === null) {
        if (!this.pickerState.isClosed) {
          this.togglePicker();
        }
      } else if (
        !this.$el.contains(e.target) && this.$el !== e.target && !this.pickerState.isClosed
      ) {
        this.togglePicker();
      }
    },

    away() {
      this.cancel();
    },
    setPickersValue(value) {
      const hms = parseHMSFromSeconds(value);
      this.pickerState.selected.hour = hms.hours;
      this.pickerState.selected.minute = hms.minutes;
      this.pickerState.selected.second = hms.seconds;
    },
  },
  watch: {
    value() {
      this.setPickersValue(this.value);
      this.originalValue = this.value;
    },
  },
  mounted() {
    this.setPickersValue(this.value);
  },
};
</script>

<style>
.vtimeselector {
  width: 100%;
  position: relative;
  font-family: sans-serif;
}

.vtimeselector__input {
  width: 100%;
  box-sizing: border-box;
}

.vtimeselector__clear {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  right: 0;
  width: 20px;
  height: 100%;
  cursor: pointer;
}

.vtimeselector__clear__ico {
  display: inline-block;
  width: 100%;
  text-align: center;
  vertical-align: middle;
  color: #a5a5a5;
}

.vtimeselector__clear:hover .vtimeselector__clear__ico {
  color: black;
}

.vtimeselector__container {
  position: absolute;
  width: 100%;
  left: 0;
  top: 100%;
  background: white;
  z-index: 999;
}


.vtimeselector__container--is-closed {
  display: none;
}

.vtimeselector__box {
  display: flex;
  height: 10em;
}

.vtimeselector__box__item {
  cursor: pointer;
}

.vtimeselector__box__list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  text-align: center;
  overflow-x: hidden;
  overflow-y: auto;
}

.vtimeselector__box__list + .vtimeselector__box__list {
  border-left: 1px solid #ffffff;
}

.vtimeselector__box__head {
  color: #a5a5a5;
  font-size: .8em;
  padding: .8em 0 .4em;
}

.vtimeselector__box__item {
  padding: .4em 0;
  font-size: 1em;
}

.vtimeselector__box__item:not(.timeselector__box__item--is-disabled):not(.timeselector__box__item--is-selected):hover {
  background: #d3d3d3;
}

.timeselector__box__item--is-highlighted {
  background: #5b64f7;
}

.timeselector__box__item--is-selected {
  background: #05cfb5;
  color: #ffffff;
}

.timeselector__box__item--is-disabled {
  cursor: auto;
  background: #f5f5f5;
  color: #a5a5a5;
}
</style>
